import React from "react";
import { motion } from "framer-motion";
import {
  HeartPulse, Home, Shield, ShoppingCart, DollarSign, Users2,
} from "lucide-react";
import PageMeta from "../site/PageMeta";
import Chapter from "../site/Chapter";

const industries = [
  {
    icon: HeartPulse,
    title: "Healthcare",
    body: "Patient-facing support and administrative work handled with the discretion and compliance-mindedness the industry requires.",
    accent: "#00E5FF",
  },
  {
    icon: Home,
    title: "Real Estate",
    body: "Transaction coordination and client communication support built around how agents and brokerages actually move deals forward.",
    accent: "#A3FF5E",
  },
  {
    icon: Shield,
    title: "Insurance",
    body: "Policy servicing, claims support, and underwriting-adjacent analytics for carriers and agencies managing volume.",
    accent: "#007AFF",
  },
  {
    icon: ShoppingCart,
    title: "eCommerce",
    body: "Listing, order, and customer support for sellers scaling faster than their internal team can keep up with.",
    accent: "#00E5FF",
  },
  {
    icon: DollarSign,
    title: "Finance",
    body: "Back-office and processing support for financial operations, including merchant cash advance servicing.",
    accent: "#A3FF5E",
  },
  {
    icon: Users2,
    title: "Staffing & Recruitment Agencies",
    body: "A recruiting operations layer for agencies who want AI-assisted sourcing and outreach without losing recruiter judgment — the original A4 Assist specialty.",
    accent: "#00E5FF",
  },
];

export default function Industries() {
  return (
    <>
      <PageMeta
        title="Industries We Serve — A4 Assist"
        description="A4 Assist supports growing businesses in healthcare, real estate, insurance, eCommerce, finance, and staffing agencies — AI-enabled workflows, specialist-owned."
      />

      <section className="pt-36 pb-14">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10">
          <Chapter
            number="00"
            eyebrow="Industries We Serve"
            title={
              <>
                Built around how your{" "}
                <span className="font-serif italic">industry</span> actually works.
              </>
            }
            subtitle="Different industries, different rhythms. We adapt the workflow to how you already run — not the other way around."
          />
        </div>
      </section>

      <section className="pb-28">
        <div className="max-w-[1400px] mx-auto px-6 sm:px-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {industries.map((it, i) => (
            <motion.div
              key={it.title}
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.05 }}
              className="rounded-2xl border border-slate-800 bg-[#0A1325]/60 p-7 hover:border-[#00E5FF]/40 transition-colors relative overflow-hidden"
              data-testid={`industry-card-${i}`}
            >
              <div
                className="absolute -top-16 -right-16 h-40 w-40 rounded-full opacity-[0.10] blur-2xl"
                style={{ background: it.accent }}
              />
              <div
                className="inline-flex h-11 w-11 items-center justify-center rounded-lg border"
                style={{
                  borderColor: `${it.accent}40`,
                  background: `${it.accent}12`,
                  color: it.accent,
                }}
              >
                <it.icon size={20} />
              </div>
              <h3 className="font-display mt-5 text-lg font-semibold text-white">
                {it.title}
              </h3>
              <p className="mt-2 text-sm leading-relaxed text-slate-400">
                {it.body}
              </p>
            </motion.div>
          ))}
        </div>
      </section>
    </>
  );
}
